import appConfig from '../config/appConfig';
import * as analyticsCategories from '../consts/analytics/analyticsCategories';
import * as browserCompatibleSelectedOptionsData from '../consts/analytics/browserCompatibleSelectedOptionsData';
import REGISTRATION_ERRORS_DATA from '../consts/analytics/registrationErrorsData';
import {makeSelectIotDeviceMergedWithConsumerProduct} from '../state/selectors/consumer';
import {selectIsAggregatedSettingsFetched, selectIsGAEnabled} from '../state/selectors/consumerAggregatedSettings';
import {selectIotDeviceData} from '../state/selectors/iotDevice';
import {getState} from '../state/store';
import urlUtils from '../utils/urlUtils';
import deviceTypesService from './device/deviceTypesService';

const pushToDataLayer = (message) => {
    const isAggregatedSettingsFetched = selectIsAggregatedSettingsFetched(getState());
    const dataLayer = window.dataLayer;
    const isGAEnabled = appConfig.getIsGAEnabled();

    if (!isGAEnabled) return;

    if (dataLayer) {
        if (isAggregatedSettingsFetched) {
            const isGAEnabledIcc = selectIsGAEnabled(getState());

            if (isGAEnabledIcc) {
                dataLayer.push(message);
            }
        } else {
            dataLayer.push(message);
        }
    }
};

const getDataLayerObject = ({eventAction, eventNonInteraction = false, eventLabel, ...rest}) => {
    return {
        event: 'iqosConnectWeb',
        eventCategory: 'Iqos Connect Web',
        eventAction,
        eventLabel,
        eventNonInteraction,
        ...rest,
    };
};

const getDataLayerObjectWithDeviceNameAndPlatform = ({type, ...props}) => {
    const deviceModel = deviceTypesService.getDeviceModelByType(type);
    const devicePlatform = deviceTypesService.getDevicePlatform(type);

    if (!type) {
        return getDataLayerObject({...props});
    }
    return getDataLayerObject({deviceModel, devicePlatform, ...props});
};

const pushAppLoadedEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'web app loaded',
            eventNonInteraction: true,
        })
    );
};

const pushBrowserCompatibilityEvent = (isSuccess, isBluefy) => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'browser compatibility check',
            eventLabel: isSuccess ? 'success' : 'fail',
            browser: isBluefy ? 'bluefy' : 'other',
            eventNonInteraction: true,
        })
    );
};

const pushBrowserDownloadEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA',
            eventLabel: 'download browser',
        })
    );
};

const pushTNCTermsOfUseInteractionEvent = (isSuccess) => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - terms of use',
            eventLabel: isSuccess ? 'agree' : 'decline',
        })
    );
};

const pushTNCDataCollectionInteractionEvent = (isSuccess) => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - data collection',
            eventLabel: isSuccess ? 'agree' : 'decline',
        })
    );
};

const pushPrePairingAddNewDeviceEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: 'add new',
        })
    );
};

const pushPrePairingPairDeviceEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: 'pair device',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushPrePairingPairRegisteredDeviceEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: 'pair device - already registered',
            eventNonInteraction: false,
        })
    );
};

const pushPrePairingPairDisconnectedDeviceEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: 'pair device - disconnected',
            eventNonInteraction: false,
        })
    );
};

const pushPrePairingSetupNewIqosEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: 'set up a new iqos',
        })
    );
};

const pushPairingRegisteredDeviceSelectionEvent = (type) => {
    const deviceModel = deviceTypesService.getDeviceModelByType(type);

    if (!deviceModel) return;

    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: `selected device - ${deviceModel} - already registered`,
            type,
        })
    );
};

const pushPairingNewDeviceSelectionEvent = (type) => {
    const deviceModel = deviceTypesService.getDeviceModelByType(type);

    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: `selected device - ${deviceModel} - new`,
            type,
        })
    );
};

const pushPairingTurnOnDeviceEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 1',
            eventLabel: 'start pairing guide',
            type,
        })
    );
};

const pushPairingBluetoothEnabledEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 2',
            eventLabel: 'request bluetooth system window',
            type,
        })
    );
};

const pushPairingDevicePairingEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step 3',
            eventLabel: 'pairing device',
            type,
        })
    );
};

const pushIoTConnectFailedEvent = (errorType, type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connect to IoT platform',
            eventLabel: errorType,
            type,
        })
    );
};

const pushPairingFailedEvent = (errorType, type, isBle) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: `${isBle ? 'Pairing' : 'Connection'} flow - step error`,
            eventLabel: errorType,
            type,
        })
    );
};

const pushPairingQuitSetupEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Pairing flow - step quit',
            eventLabel: 'quit set up',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushActivationDeviceActivatingEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Activation flow - step 1',
            eventLabel: 'activating device',
            type,
        })
    );
};

const pushActivationDeviceActivatedSuccessEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Activation flow - step 2',
            eventLabel: 'successfully activated',
            type,
        })
    );
};

const pushErrorEvent = ({errorCode, errorType = '', isActivationEvent, errorMessage = ''}) => {
    const iotProduct = makeSelectIotDeviceMergedWithConsumerProduct()(getState());
    const {device} = iotProduct || {};
    const {type} = device || {};
    const separator = errorType || errorMessage ? ' | ' : '';

    return pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: `${isActivationEvent ? 'Activation' : 'Registration'} flow - step ${
                isActivationEvent ? '2 - ' : ''
            }error`,
            eventLabel: `${isActivationEvent ? errorType : errorCode + separator + (errorType || errorMessage)}`,
            type,
        })
    );
};

const pushUsageMyDevicesScreenviewEvent = () => {
    const urlPath = urlUtils.getCurrentPathname();

    pushToDataLayer({
        event: 'iqosConnectWebScreenview',
        urlPath,
    });
};

const pushUsageSettingsInteractionEvent = (category, messageUpdate) => {
    const iotDevice = selectIotDeviceData(getState());
    const {device} = iotDevice;
    const {type} = device || {};
    const data = {
        eventAction: 'Click - CTA - settings update',
        eventLabel: category,
        type,
    };

    if (messageUpdate) {
        data.messageUpdate = messageUpdate;
    }

    pushToDataLayer(getDataLayerObjectWithDeviceNameAndPlatform(data));
};

const pushUsageMyDevicesEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Device list',
            eventLabel: 'device list',
        })
    );
};

const pushUsageDeviceSettingsEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Device Settings',
            eventLabel: 'device settings',
        })
    );
};

const pushUsageSettingsRenameEvent = () => pushUsageSettingsInteractionEvent(analyticsCategories.AC_RENAME);
const pushUsageSettingsAutoCleaningEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_AUTO_CLEANING);
const pushUsageSettingsDiagnosticEvent = () => pushUsageSettingsInteractionEvent(analyticsCategories.AC_DIAGNOSTIC);
const pushUsageSettingsVibrationsEvent = (vibrationMessageData) => {
    const vibrationItemsResultList = [];
    const isVibrationItemExist = (vibrationItem) => vibrationItem !== undefined;

    for (const [key, value] of Object.entries(vibrationMessageData)) {
        switch (key) {
            case 'all_holder_vibrations':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`all holder vibration: ${value}`);
                break;
            case 'battery_charged':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`battery charged: ${value}`);
                break;
            case 'start_heating':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`start heating: ${value}`);
                break;
            case 'fully_heated':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`fully heated: ${value}`);
                break;
            case 'almost_finished':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`almost finished: ${value}`);
                break;
            case 'finished':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`finished: ${value}`);
                break;
            case 'extended_haptic_profile':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`extended haptic profile: ${value}`);
                break;
            case 'start_charging':
                if (isVibrationItemExist(value)) vibrationItemsResultList.push(`start charging: ${value}`);
                break;
            default:
                break;
        }
    }

    const messageUpdate = `Vibration = ${vibrationItemsResultList.join(',')}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_VIBRATIONS, messageUpdate);
};
const pushUsageSettingsLedModeEvent = (intensity) => {
    const messageUpdate = `LightMode = ${intensity}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_LIGHT_MODE, messageUpdate);
};
const pushUsageSettingsFindDeviceEvent = () => pushUsageSettingsInteractionEvent(analyticsCategories.AC_FIND_DEVICE);
const pushUsageSettingsFwUpdateEvent = () => {
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_FW_UPDATE);
};
const pushUsageSettingsResponsiveDrawModeEvent = (responsiveDrawProfile) => {
    const messageUpdate = `Responsive draw mode = ${responsiveDrawProfile}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_RESPONSIVE_DRAW, messageUpdate);
};
const pushUsageSettingsVapeCloudSizeModeEvent = (deviceVapeCloudSize) => {
    const messageUpdate = `Cloud size = ${deviceVapeCloudSize}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_VAPE_CLOUD_sIZE, messageUpdate);
};
const pushUsageSettingsTipsAndTutorialsEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_TIPS_AND_TUTORIALS);
const pushUsageSettingsDeviceResetEvent = () => pushUsageSettingsInteractionEvent(analyticsCategories.AC_RESET);
const pushUsageSettingsUnpairDeviceEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_UNPAIR_DEVICE);
const pushUsageSettingsDisconnectDeviceEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_DISCONNECT_DEVICE);
const pushUsageSettingsStoreLocatorEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_STORE_LOCATOR);
const pushUsageSettingsPreferenceCenterEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_PREFERENCE_CENTER);
const pushUsageSettingsLegalAndPolicyEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_LEGAL_AND_POLICY);
const pushUsageSettingsPauseModeGuideEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_PAUSE_MODE_GUIDE);
const pushUsageSettingsAboutMyDeviceEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_ABOUT_MY_DEVICE);
const pushUsageSettingsLockDeviceEvent = () => {
    const messageUpdate = 'lockDevice';

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_LOCK_MY_DEVICE, messageUpdate);
};
const pushUsageSettingsUnlockDeviceEvent = () => {
    const messageUpdate = 'unlockDevice';

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_UNLOCK_MY_DEVICE, messageUpdate);
};
const pushUsageSettingsAutoStartEvent = (autostartMode) => {
    const messageUpdate = `Autostart heating = ${autostartMode}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_AUTOSTART, messageUpdate);
};
const pushUsageSettingsFlexPuffEvent = (mode) => {
    const messageUpdate = `FlexPuff = ${mode}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_DEVICE_FLEXPUFF, messageUpdate);
};
const pushUsageSettingsFlexBatteryEvent = (mode) => {
    const messageUpdate = `FlexBattery = ${mode}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_DEVICE_FLEXBATTERY, messageUpdate);
};
const pushUsageSettingsPauseModeEvent = (mode) => {
    const messageUpdate = `Pause mode = ${mode}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_DEVICE_PAUSE_MODE, messageUpdate);
};
const pushUsageSettingsSmartGesturesEvent = (gestureModeSettings) => {
    const gestureList = [];

    gestureModeSettings.forEach((gesture) => {
        const gestureMode = gesture?.holder;
        const {status_gesture_double_tap_enabled, status_gesture_pickup_enabled} = gestureMode || {};

        if (status_gesture_double_tap_enabled !== undefined) {
            gestureList.push(`Double tap gesture: ${status_gesture_double_tap_enabled}`);
        }

        if (status_gesture_pickup_enabled !== undefined) {
            gestureList.push(`Lift up gesture : ${status_gesture_pickup_enabled}`);
        }
    });

    const messageUpdate = gestureList.join(' | ');

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_SMART_GESTURES, messageUpdate);
};
const pushUsageSettingsResetDeviceEvent = () => pushUsageSettingsInteractionEvent(analyticsCategories.AC_RESET_DEVICE);
const pushUsageSettingsUpdateFirmwareEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_UPDATE_FIRMWARE);

const pushUsageSettingsUsageEvent = () => pushUsageSettingsInteractionEvent(analyticsCategories.AC_USAGE);

const pushUsageSettingsTradeInEvent = () => pushUsageSettingsInteractionEvent(analyticsCategories.AC_TRADE_IN);
const pushUsageSettingsCheckUpgradeFirmwareEvent = () =>
    pushUsageSettingsInteractionEvent(analyticsCategories.AC_CHECK_FIRMWARE_UPGRADE);

const pushUsageSettingsVapingSessionEvent = (vapingSessionData) => {
    const {puffNumber, puffingTime} = vapingSessionData || {};
    const messageUpdate = `Vaping session = puff number: ${puffNumber},puff time : ${puffingTime}`;

    pushUsageSettingsInteractionEvent(analyticsCategories.AC_VAPING_SESSION_IN, messageUpdate);
};

const pushActivationFailedError = (errorData) => {
    const {type} = errorData;

    pushErrorEvent({errorType: type, isActivationEvent: true});
};

const pushRegistrationFailedError = (key) => {
    pushErrorEvent({errorCode: REGISTRATION_ERRORS_DATA[key].code, errorType: REGISTRATION_ERRORS_DATA[key].type});
};

const pushSpecificRegistrationFailedError = (errorItem) => {
    const {errorCode, errorMessage} = errorItem || {};

    pushErrorEvent({errorCode, errorMessage});
};

const pushRegistrationSuccessfullyEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Registration flow - successfully',
            eventLabel: 'Registration flow - successfully',
            type,
        })
    );
};

const pushRegistrationFlowStartEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Registration flow - start',
            eventLabel: 'Registration flow - start',
            type,
        })
    );
};

const pushDeviceDiagnosticContactUsEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - device diagnostic',
            eventLabel: 'contact us',
            eventNonInteraction: false,
        })
    );
};

const pushDeviceConnectCareContactUsEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - connect care',
            eventLabel: 'contact us',
            eventNonInteraction: false,
            type,
        })
    );
};

const getCompatibleSelectedOption = (link) => {
    switch (link) {
        case appConfig.getLinkAndroidStoreIqos():
            return browserCompatibleSelectedOptionsData.ANDROID_APP;
        case appConfig.getLinkAndroidStoreChrome():
            return browserCompatibleSelectedOptionsData.CHROME_MOBILE;
        case appConfig.getLinkIosStoreIqos():
            return;
        case appConfig.getLinkIosStoreBluenet():
            return browserCompatibleSelectedOptionsData.BLUEFY;
        case appConfig.getLinkMacWidget():
            return browserCompatibleSelectedOptionsData.WIDGET_MAC_OS_APP;
        case appConfig.getLinkWindowsWidget():
            return browserCompatibleSelectedOptionsData.WIDGET_WINDOWS_APP;
        case appConfig.getLinkDesktopChrome():
            return browserCompatibleSelectedOptionsData.CHROME_DESKTOP;
        case appConfig.getLinkDesktopEdge():
            return browserCompatibleSelectedOptionsData.EDGE_DESKTOP;
        case appConfig.getLinkAppGalleryEdge():
            return browserCompatibleSelectedOptionsData.EDGE_APP_GALLERY;
        case appConfig.getLinkAndroidStoreEdge():
            return browserCompatibleSelectedOptionsData.EDGE_MOBILE;
        default:
            return link;
    }
};

const pushBrowserCompatibleEvent = (link, isCompatible) => {
    const selectedOption = getCompatibleSelectedOption(link);

    pushToDataLayer(
        getDataLayerObject({
            eventAction: `Click - CTA - browser is ${isCompatible ? '' : 'not '}compatible`,
            eventLabel: `selected ${isCompatible ? 'app' : 'option'} - ${selectedOption}`,
        })
    );
};

const pushNotCompatibleIosVersionEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'iOS version is not compatible',
            eventLabel: 'iOS version is not compatible',
        })
    );
};

const pushConnectIotPlatformEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connect to IoT platform',
            eventLabel: 'Connect to IoT platform',
            type,
        })
    );
};

const pushConnectIotPlatformErrorEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connect to IoT platform - error',
            eventLabel: 'ERROR_CONNECTION_FAILED',
            type,
        })
    );
};

const pushErrorHandlingEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Something went wrong',
            eventLabel: 'Something went wrong',
            eventNonInteraction: false,
        })
    );
};

const pushPreferenceCenterEvent = (isSuccess) => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Preference center',
            eventLabel: 'Preference center',
            eventNonInteraction: false,
        })
    );
};

const pushPreferenceCenterContinueEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - preference center',
            eventLabel: 'preference center - continue',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushBenefitSetUpNewIqosEvent = () => {
    pushToDataLayer(
        getDataLayerObject({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: 'Benefit - set up a new iqos',
            eventNonInteraction: false,
        })
    );
};

const pushUsbRequiredEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'No-BLE connection',
            eventLabel: 'no-BLE connection',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushUsbRequiredConnectAnotherEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - No-BLE connection',
            eventLabel: 'no-BLE connection - continue',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushConnectionSelectionChooseEvent = (type, isUsb) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - pre-pairing',
            eventLabel: `select ${isUsb ? 'USB' : 'BLE'}`,
            eventNonInteraction: false,
            type,
        })
    );
};

const pushConnectionFlowFirstStepEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connection flow - step 1',
            eventLabel: 'connection guide',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushConnectionFlowFirstStepContinueEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connection flow - step 1',
            eventLabel: 'connection guide - continue',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushConnectionUsbEnabledEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connection flow - step 2',
            eventLabel: 'request usbhid system window',
            type,
        })
    );
};

const pushConnectingDeviceConnectingEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connection flow - step 3',
            eventLabel: 'connecting device',
            type,
        })
    );
};

const pushSuccessPairingConnectingDeviceEvent = (type, isBle) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connect to IoT platform',
            eventLabel: `all set - successfully ${isBle ? 'paired' : 'connected'}`,
            eventNonInteraction: false,
            type,
        })
    );
};

const pushSuccessPairingConnectingDeviceNotificationEvent = (type, isBle) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connect to IoT platform',
            eventLabel: `successfully ${isBle ? 'paired' : 'connected'}`,
            eventNonInteraction: false,
            type,
        })
    );
};

const pushBleReconnectionNotificationEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'BLE - reconnection',
            eventLabel: 'lost connection',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushUsbReconnectionEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'USB - reconnection',
            eventLabel: 'lost connection',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushBleReconnectionGuideEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'BLE - reconnection',
            eventLabel: 'reconnection guide',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushDeviceReconnectNotificationEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Reconnect to IoT platform - successfully',
            eventLabel: 'Reconnect to IoT platform - successfully',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushNonCriticalDeviceReconnectionErrorEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'BLE - reconnection',
            eventLabel: 'non-critical error',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushGeneralDeviceReconnectionErrorEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'General error - lost connection',
            eventLabel: 'general error - lost connection',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushRegistrationPurchaseDateEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Registration flow - purchase date',
            eventLabel: 'Registration flow - purchase date',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushRegistrationUnregisteredHolderEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Registration flow - unregistered holder',
            eventLabel: 'Registration flow - unregistered holder',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushRegistrationNonNativeEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Registration flow - non-native',
            eventLabel: 'Registration flow - non-native',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushManualRegistrationEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Manual Registration flow - step error',
            eventLabel: 'Manual Registration flow - step error',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushManualRegistrationFlowEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Registration flow - manual registration',
            eventLabel: 'Registration flow - manual registration',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushActivationFlowDeviceListEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Activation flow - step 2 - Device list',
            eventLabel: 'Activation flow - step 2 - device list - continue',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushActivationFlowContinueManualEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - Activation flow - step 2 - error',
            eventLabel: 'Activation flow - step 2 - error - continue',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushUnavailableUWMEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'UWM - something went wrong',
            eventLabel: 'UWM - something went wrong',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUCheckingEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - checking fwu',
            eventLabel: 'checking fwu',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUNewerAvailableEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - checking fwu',
            eventLabel: 'newer fw is available',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUStartEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - start fwu',
            eventLabel: 'start fwu',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUUnavailableEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - checking fwu',
            eventLabel: 'fw is unavailable',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUStartConfirmEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - start fwu',
            eventLabel: 'start fwu - confirm',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUChargeDeviceEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - start fwu',
            eventLabel: 'start fwu - charge device',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUInProgressEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - Installing update',
            eventLabel: 'fwu in progress',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUReconnectActionEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - Installing update',
            eventLabel: 'reconnect device',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUFailedEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - failed',
            eventLabel: 'fwu in progress',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUPartialSuccessEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - Installing update',
            eventLabel: 'part of fwu is installed',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUPartialSuccessContinueEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - Continue fwu',
            eventLabel: 'continue fwu',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUSuccessFailedResetEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - successfully',
            eventLabel: 'reset failed',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUUpToDateEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - successfully',
            eventLabel: 'fwu is up to date',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushFWUFailedCantUpgradeEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'FWU flow - failed',
            eventLabel: 'fwu is can’t upgrade',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushSpecialFlowSelectLedEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 1',
            eventLabel: 'Select LED',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushSpecialFlowHolderIssueEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 2',
            eventLabel: 'Special flow for holder DUO - holder issue',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushSpecialFlowNextEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 2',
            eventLabel: 'Special flow for holder DUO - next',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushSpecialFlowGuideEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 3',
            eventLabel: 'Special flow for holder DUO - Guide',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushSpecialFlowFinalizingEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Special flow for holder DUO - step 4',
            eventLabel: 'Special flow for holder DUO - finalizing',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushADPhaseEvent = (type, isPhase2, screen) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: `AD Phase ${isPhase2 ? '2' : '1'}`,
            eventLabel: screen,
            eventNonInteraction: false,
            type,
        })
    );
};

const pushConnectCareResultEvent = (type, error, priorityError) => {
    let parsedError;

    switch (error) {
        case 1:
            parsedError = 'M1';
            break;
        case 2:
            parsedError = 'M2';
            break;
        case 3:
            parsedError = 'M3';
            break;
        case 4:
            parsedError = 'M4';
            break;
        default:
            parsedError = 'No error';
    }

    const errorList = [parsedError];

    if (priorityError) {
        errorList.push(`${priorityError} code`);
    }

    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Connect care - Resolution screen',
            eventLabel: errorList.join(' | '),
            eventNonInteraction: false,
            type,
        })
    );
};

const pushUnregistrationStartEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - unregistration',
            eventLabel: 'unregister this device',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushUnregistrationFlowErrorEvent = (type, error) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Unregistration flow - error',
            eventLabel: `Unregistration error - ${error}`,
            eventNonInteraction: false,
            type,
        })
    );
};

const pushUnregistrationFlowSuccessEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Unregistration flow - successfully',
            eventLabel: 'Unregistration flow - successfully',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushDeactivationnEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Click - CTA - deactivation',
            eventLabel: 'deactivate this device',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushDeactivationFlowErrorEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Deactivation flow - error',
            eventLabel: 'Deactivation flow - error',
            eventNonInteraction: false,
            type,
        })
    );
};

const pushDeactivationSuccessfullyEvent = (type) => {
    pushToDataLayer(
        getDataLayerObjectWithDeviceNameAndPlatform({
            eventAction: 'Deactivation flow - successfully',
            eventLabel: 'Deactivation flow - successfully',
            eventNonInteraction: false,
            type,
        })
    );
};

export default {
    pushDeactivationSuccessfullyEvent,
    pushDeactivationnEvent,
    pushDeactivationFlowErrorEvent,
    pushUnregistrationFlowSuccessEvent,
    pushUnregistrationFlowErrorEvent,
    pushUnregistrationStartEvent,
    pushConnectCareResultEvent,
    pushDeviceConnectCareContactUsEvent,
    pushADPhaseEvent,
    pushSpecialFlowFinalizingEvent,
    pushSpecialFlowGuideEvent,
    pushSpecialFlowNextEvent,
    pushSpecialFlowHolderIssueEvent,
    pushSpecialFlowSelectLedEvent,
    pushFWUFailedCantUpgradeEvent,
    pushFWUUpToDateEvent,
    pushFWUSuccessFailedResetEvent,
    pushFWUPartialSuccessContinueEvent,
    pushFWUPartialSuccessEvent,
    pushFWUFailedEvent,
    pushFWUReconnectActionEvent,
    pushFWUInProgressEvent,
    pushFWUChargeDeviceEvent,
    pushFWUStartConfirmEvent,
    pushFWUUnavailableEvent,
    pushFWUStartEvent,
    pushFWUNewerAvailableEvent,
    pushFWUCheckingEvent,
    pushUnavailableUWMEvent,
    pushUsageSettingsVapingSessionEvent,
    pushUsageSettingsDisconnectDeviceEvent,
    pushUsageSettingsCheckUpgradeFirmwareEvent,
    pushUsageSettingsTradeInEvent,
    pushUsageSettingsUsageEvent,
    pushUsageSettingsUpdateFirmwareEvent,
    pushActivationFlowContinueManualEvent,
    pushActivationFlowDeviceListEvent,
    pushManualRegistrationFlowEvent,
    pushSpecificRegistrationFailedError,
    pushManualRegistrationEvent,
    pushRegistrationNonNativeEvent,
    pushRegistrationUnregisteredHolderEvent,
    pushRegistrationPurchaseDateEvent,
    pushGeneralDeviceReconnectionErrorEvent,
    pushNonCriticalDeviceReconnectionErrorEvent,
    pushDeviceReconnectNotificationEvent,
    pushBleReconnectionGuideEvent,
    pushUsbReconnectionEvent,
    pushBleReconnectionNotificationEvent,
    pushIoTConnectFailedEvent,
    pushSuccessPairingConnectingDeviceNotificationEvent,
    pushSuccessPairingConnectingDeviceEvent,
    pushConnectingDeviceConnectingEvent,
    pushConnectionUsbEnabledEvent,
    pushConnectionFlowFirstStepContinueEvent,
    pushConnectionFlowFirstStepEvent,
    pushConnectionSelectionChooseEvent,
    pushUsbRequiredConnectAnotherEvent,
    pushUsbRequiredEvent,
    pushBenefitSetUpNewIqosEvent,
    pushPreferenceCenterContinueEvent,
    pushPreferenceCenterEvent,
    pushErrorHandlingEvent,
    pushActivationDeviceActivatedSuccessEvent,
    pushActivationDeviceActivatingEvent,
    pushActivationFailedError,
    pushAppLoadedEvent,
    pushBrowserCompatibleEvent,
    pushBrowserCompatibilityEvent,
    pushBrowserDownloadEvent,
    pushConnectIotPlatformErrorEvent,
    pushConnectIotPlatformEvent,
    pushDeviceDiagnosticContactUsEvent,
    pushNotCompatibleIosVersionEvent,
    pushPairingBluetoothEnabledEvent,
    pushPairingDevicePairingEvent,
    pushPairingRegisteredDeviceSelectionEvent,
    pushPairingNewDeviceSelectionEvent,
    pushPairingFailedEvent,
    pushPairingQuitSetupEvent,
    pushPairingTurnOnDeviceEvent,
    pushPrePairingAddNewDeviceEvent,
    pushPrePairingPairDeviceEvent,
    pushPrePairingPairDisconnectedDeviceEvent,
    pushPrePairingPairRegisteredDeviceEvent,
    pushPrePairingSetupNewIqosEvent,
    pushRegistrationFailedError,
    pushRegistrationFlowStartEvent,
    pushRegistrationSuccessfullyEvent,
    pushTNCDataCollectionInteractionEvent,
    pushTNCTermsOfUseInteractionEvent,
    pushUsageDeviceSettingsEvent,
    pushUsageMyDevicesEvent,
    pushUsageMyDevicesScreenviewEvent,
    pushUsageSettingsAboutMyDeviceEvent,
    pushUsageSettingsAutoCleaningEvent,
    pushUsageSettingsDeviceResetEvent,
    pushUsageSettingsDiagnosticEvent,
    pushUsageSettingsFindDeviceEvent,
    pushUsageSettingsFwUpdateEvent,
    pushUsageSettingsLedModeEvent,
    pushUsageSettingsLegalAndPolicyEvent,
    pushUsageSettingsPauseModeGuideEvent,
    pushUsageSettingsLockDeviceEvent,
    pushUsageSettingsPreferenceCenterEvent,
    pushUsageSettingsRenameEvent,
    pushUsageSettingsResponsiveDrawModeEvent,
    pushUsageSettingsStoreLocatorEvent,
    pushUsageSettingsTipsAndTutorialsEvent,
    pushUsageSettingsUnlockDeviceEvent,
    pushUsageSettingsUnpairDeviceEvent,
    pushUsageSettingsVapeCloudSizeModeEvent,
    pushUsageSettingsVibrationsEvent,
    pushUsageSettingsAutoStartEvent,
    pushUsageSettingsFlexPuffEvent,
    pushUsageSettingsFlexBatteryEvent,
    pushUsageSettingsPauseModeEvent,
    pushUsageSettingsSmartGesturesEvent,
    pushUsageSettingsResetDeviceEvent,
};
